<!-- src/lib/components/WorkspaceMembersSettings.svelte -->
<script lang="ts">
  import { onMount } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import {
    membersStore,
    loadMembers,
    inviteMember,
    removeMember,
    updateMemberRole,
    revokeInvitation,
  } from '../stores/members.svelte.ts';
  import { authStore } from '../stores/auth.svelte.ts';
  import { isCloudMode } from '../supabase.ts';

  let inviteEmail   = $state('');
  let inviteRole    = $state('member');
  let inviting      = $state(false);
  let inviteError   = $state<string | null>(null);
  let inviteSuccess = $state(false);
  let copied        = $state<string | null>(null);

  const workspaceId = $derived(pmStore.activeWorkspaceId);

  onMount(() => {
    if (workspaceId) void loadMembers(workspaceId);
  });

  async function handleInvite() {
    if (!inviteEmail.trim() || !workspaceId) return;
    inviting = true;
    inviteError = null;
    const err = await inviteMember(workspaceId, inviteEmail, inviteRole);
    inviting = false;
    if (err) { inviteError = err; return; }
    inviteEmail = '';
    inviteSuccess = true;
    setTimeout(() => { inviteSuccess = false; }, 3000);
  }

  function inviteLink(token: string): string {
    return `${window.location.origin}/invite/${token}`;
  }

  async function copyLink(token: string) {
    await navigator.clipboard.writeText(inviteLink(token));
    copied = token;
    setTimeout(() => { copied = null; }, 2000);
  }

  const currentUserRole = $derived(
    membersStore.members.find(m => m.user_id === authStore.user?.id)?.role
  );

  const isAdmin = $derived(
    currentUserRole === 'owner' || currentUserRole === 'admin'
  );
</script>

<div class="members-page">
  {#if !isCloudMode}
    <div class="cloud-only-notice">
      <span class="notice-icon">☁</span>
      <div>
        <strong>Cloud mode only</strong>
        <p>Member management requires a Supabase cloud backend. Self-hosted instances manage access via server configuration.</p>
      </div>
    </div>
  {:else}
    <!-- Current members -->
    <section class="members-section">
      <h3 class="section-heading">Members</h3>
      {#if membersStore.loading}
        <p class="loading-hint">Loading…</p>
      {:else if membersStore.members.length === 0}
        <p class="empty-hint">No members yet.</p>
      {:else}
        <ul class="member-list">
          {#each membersStore.members as m (m.user_id)}
            <li class="member-row">
              <div class="member-avatar">
                {#if m.user?.avatar_url}
                  <img src={m.user.avatar_url} alt={m.user.display_name ?? m.user.email} />
                {:else}
                  <span>{(m.user?.display_name ?? m.user?.email ?? '?')[0].toUpperCase()}</span>
                {/if}
              </div>
              <div class="member-info">
                <span class="member-name">{m.user?.display_name ?? m.user?.email}</span>
                <span class="member-email">{m.user?.email}</span>
              </div>
              {#if isAdmin && m.user_id !== authStore.user?.id}
                <select
                  class="role-select"
                  value={m.role}
                  onchange={(e) => void updateMemberRole(workspaceId!, m.user_id, (e.currentTarget as HTMLSelectElement).value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <button
                  class="remove-btn"
                  onclick={() => void removeMember(workspaceId!, m.user_id)}
                  title="Remove member"
                >×</button>
              {:else}
                <span class="role-chip role-{m.role}">{m.role}</span>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    <!-- Pending invitations -->
    {#if membersStore.invitations.length > 0}
      <section class="members-section">
        <h3 class="section-heading">Pending invitations</h3>
        <ul class="invite-list">
          {#each membersStore.invitations as inv (inv.id)}
            <li class="invite-row">
              <span class="invite-email">{inv.email}</span>
              <span class="role-chip role-{inv.role}">{inv.role}</span>
              <span class="invite-expires">Expires {new Date(inv.expires_at).toLocaleDateString()}</span>
              <button class="copy-btn" onclick={() => void copyLink(inv.token)}>
                {copied === inv.token ? '✓ Copied' : 'Copy link'}
              </button>
              <button
                class="remove-btn"
                onclick={() => void revokeInvitation(inv.id, workspaceId!)}
                title="Revoke invitation"
              >×</button>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <!-- Invite form -->
    {#if isAdmin}
      <section class="members-section">
        <h3 class="section-heading">Invite someone</h3>
        <form
          class="invite-form"
          onsubmit={(e) => { e.preventDefault(); void handleInvite(); }}
        >
          <input
            class="invite-input"
            type="email"
            bind:value={inviteEmail}
            placeholder="colleague@company.com"
            disabled={inviting}
          />
          <select class="invite-role-select" bind:value={inviteRole} disabled={inviting}>
            <option value="viewer">Viewer</option>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button
            class="invite-btn"
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? 'Inviting…' : 'Send invite'}
          </button>
        </form>
        {#if inviteError}
          <p class="invite-error">{inviteError}</p>
        {/if}
        {#if inviteSuccess}
          <p class="invite-success">Invitation created! Share the link with your teammate.</p>
        {/if}
      </section>
    {/if}
  {/if}
</div>

<style>
  .members-page {
    padding: 1.5rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  /* ── Cloud-only notice ───────────────────────────────────── */
  .cloud-only-notice {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border: 1px solid oklch(0.70 0.14 85 / 0.40);
    border-radius: 8px;
    background: oklch(0.18 0.04 85 / 0.18);
    color: oklch(0.82 0.10 85);
  }
  .notice-icon {
    font-size: 1.4rem;
    line-height: 1;
    flex-shrink: 0;
  }
  .cloud-only-notice strong {
    display: block;
    font-size: 0.92rem;
    color: oklch(0.90 0.12 85);
    margin-bottom: 0.25rem;
  }
  .cloud-only-notice p {
    margin: 0;
    font-size: 0.84rem;
    color: oklch(0.72 0.08 85 / 0.85);
    line-height: 1.5;
  }

  /* ── Sections ────────────────────────────────────────────── */
  .members-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .section-heading {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.80 0.08 310);
    margin: 0 0 0.5rem;
  }

  .loading-hint,
  .empty-hint {
    font-size: 0.84rem;
    color: oklch(0.55 0.04 310 / 0.65);
    margin: 0;
  }

  /* ── Member list ─────────────────────────────────────────── */
  .member-list,
  .invite-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid oklch(0.22 0.04 275 / 0.25);
    border-radius: 8px;
    overflow: hidden;
  }

  .member-row,
  .invite-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.65rem 0.9rem;
    border-bottom: 1px solid oklch(0.22 0.04 275 / 0.25);
  }
  .member-row:last-child,
  .invite-row:last-child {
    border-bottom: none;
  }

  /* ── Avatar ──────────────────────────────────────────────── */
  .member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: oklch(0.20 0.04 275);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }
  .member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .member-avatar span {
    font-size: 0.8rem;
    font-weight: 600;
    color: oklch(0.78 0.12 310);
  }

  /* ── Member info ─────────────────────────────────────────── */
  .member-info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    flex: 1;
    min-width: 0;
  }
  .member-name {
    font-size: 0.88rem;
    font-weight: 500;
    color: oklch(0.88 0.06 310);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .member-email {
    font-size: 0.76rem;
    color: oklch(0.58 0.04 310 / 0.70);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Role chips ──────────────────────────────────────────── */
  .role-chip {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .role-chip.role-owner {
    background: oklch(0.28 0.08 310 / 0.50);
    color: oklch(0.82 0.12 310);
  }
  .role-chip.role-admin {
    background: oklch(0.25 0.08 250 / 0.50);
    color: oklch(0.75 0.12 250);
  }
  .role-chip.role-member {
    background: oklch(0.20 0.03 275 / 0.50);
    color: oklch(0.65 0.05 275);
  }
  .role-chip.role-viewer {
    background: oklch(0.18 0.02 275 / 0.40);
    color: oklch(0.52 0.04 275 / 0.80);
  }

  /* ── Role select ─────────────────────────────────────────── */
  .role-select {
    font-size: 0.80rem;
    background: oklch(0.16 0.03 275);
    color: oklch(0.82 0.06 310);
    border: 1px solid oklch(0.28 0.04 275 / 0.50);
    border-radius: 5px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    appearance: auto;
    flex-shrink: 0;
  }
  .role-select:focus {
    outline: 2px solid oklch(0.65 0.14 310 / 0.55);
    outline-offset: 1px;
  }

  /* ── Remove button ───────────────────────────────────────── */
  .remove-btn {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid oklch(0.65 0.12 20 / 0.30);
    border-radius: 4px;
    color: oklch(0.62 0.10 20 / 0.80);
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 100ms ease, color 100ms ease;
  }
  .remove-btn:hover {
    background: oklch(0.22 0.06 20 / 0.35);
    color: oklch(0.78 0.14 20);
    border-color: oklch(0.65 0.14 20 / 0.55);
  }

  /* ── Invite row extras ───────────────────────────────────── */
  .invite-email {
    font-size: 0.86rem;
    color: oklch(0.82 0.05 310);
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .invite-expires {
    font-size: 0.76rem;
    color: oklch(0.55 0.04 275 / 0.70);
    flex-shrink: 0;
  }

  /* ── Copy button ─────────────────────────────────────────── */
  .copy-btn {
    font-size: 0.78rem;
    padding: 0.2rem 0.6rem;
    background: oklch(0.18 0.04 275 / 0.60);
    border: 1px solid oklch(0.32 0.05 275 / 0.45);
    border-radius: 5px;
    color: oklch(0.72 0.07 310);
    cursor: pointer;
    flex-shrink: 0;
    transition: background 100ms ease, color 100ms ease;
  }
  .copy-btn:hover {
    background: oklch(0.22 0.05 275 / 0.70);
    color: oklch(0.88 0.09 310);
  }

  /* ── Invite form ─────────────────────────────────────────── */
  .invite-form {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .invite-input {
    flex: 1;
    min-width: 200px;
    padding: 0.45rem 0.75rem;
    background: oklch(0.14 0.025 275);
    border: 1px solid oklch(0.28 0.04 275 / 0.50);
    border-radius: 6px;
    color: oklch(0.88 0.06 310);
    font-size: 0.86rem;
  }
  .invite-input::placeholder {
    color: oklch(0.50 0.03 275 / 0.60);
  }
  .invite-input:focus {
    outline: 2px solid oklch(0.65 0.14 310 / 0.55);
    outline-offset: 1px;
  }
  .invite-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .invite-role-select {
    font-size: 0.82rem;
    background: oklch(0.16 0.03 275);
    color: oklch(0.82 0.06 310);
    border: 1px solid oklch(0.28 0.04 275 / 0.50);
    border-radius: 6px;
    padding: 0.45rem 0.65rem;
    cursor: pointer;
    appearance: auto;
    flex-shrink: 0;
  }
  .invite-role-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .invite-btn {
    padding: 0.45rem 1rem;
    background: oklch(0.48 0.16 310);
    border: none;
    border-radius: 6px;
    color: oklch(0.97 0.02 310);
    font-size: 0.84rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 100ms ease;
  }
  .invite-btn:hover:not(:disabled) {
    background: oklch(0.55 0.18 310);
  }
  .invite-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .invite-error {
    font-size: 0.82rem;
    color: oklch(0.72 0.16 20);
    margin: 0.35rem 0 0;
  }
  .invite-success {
    font-size: 0.82rem;
    color: oklch(0.72 0.14 150);
    margin: 0.35rem 0 0;
  }
</style>
